import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { generalMessages } from 'src/utils/messages';
import { booleanBadgeOptions } from 'src/utils/constants';
import { useListServicePlanBillingPolicies } from 'src/features/servicePlanBillingPolicies/api/useListServicePlanBillingPolicies';
import { BILLING_STATUS, ServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/utils/types';
import { billingPoliciesMessages } from 'src/features/servicePlanBillingPolicies/utils/messages';
import { useParams } from 'react-router-dom';
import CreateServicePlanBillingPolicyFormModal from 'src/features/servicePlanBillingPolicies/components/forms/CreateServicePlanBillingPolicyFormModal';
import {
    BILLING_CYCLE_BADGE_OPTIONS,
    BILLING_STATUS_BADGE_OPTIONS,
    BILLING_TIME_BADGE_OPTIONS,
    MEASUREMENT_TYPE_MAP,
    METERED_BILLING_STRATEGY_MAP,
    STATEFUL_BILLING_STRATEGY_MAP,
} from 'src/features/servicePlanBillingPolicies/utils/constants';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import CellServicePlanBillingPolicyActions from 'src/features/servicePlanBillingPolicies/components/tables/CellServicePlanBillingPolicyActions';
import useFilters from 'src/registry/hooks/useFilters';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import CellDate from 'src/registry/components/table/cells/CellDate';
import useSorting from 'src/registry/hooks/useSorting';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type ServicePlanBillingPoliciesTableProps = {
    isServicePlanItemActive?: boolean;
};

const ServicePlanBillingPoliciesTable = ({ isServicePlanItemActive }: ServicePlanBillingPoliciesTableProps) => {
    const { formatMessage } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'version_number', desc: false }],
    });

    const [dateNow] = React.useState(() => Date.now());

    const {
        data: servicePlanBillingPolicies,
        isLoading,
        isError,
    } = useListServicePlanBillingPolicies({
        servicePlanItemId,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        config: { enabled: !!servicePlanItemId },
    });

    const columnHelper = createColumnHelper<ServicePlanBillingPolicy>();

    const columns = [
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(servicePlansMessages.version),
            },
            maxSize: 45,
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: BILLING_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            cell: CellDate,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            cell: CellDate,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('billing_cycle', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(billingPoliciesMessages.billingCycle),
                badgeOptions: BILLING_CYCLE_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('billing_time', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(billingPoliciesMessages.billingTime),
                badgeOptions: BILLING_TIME_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('measurement_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const translatedMeasurementType = formatMessage(MEASUREMENT_TYPE_MAP[row.original.measurement_type]);
                const translatedStatefulBilling =
                    row.original.stateful_billing_strategy &&
                    formatMessage(STATEFUL_BILLING_STRATEGY_MAP[row.original.stateful_billing_strategy]);
                const translatedMeteredBilling =
                    row.original.metered_billing_strategy &&
                    formatMessage(METERED_BILLING_STRATEGY_MAP[row.original.metered_billing_strategy]);

                const billingStrategyToDisplay = row.original.stateful_billing_strategy
                    ? `- ${translatedStatefulBilling}`
                    : row.original?.metered_billing_strategy
                      ? `- ${translatedMeteredBilling}`
                      : null;
                return (
                    <div className="flex flex-row whitespace-nowrap">
                        <div className="pr-1">{translatedMeasurementType}</div>
                        <div>{billingStrategyToDisplay}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(billingPoliciesMessages.measurement),
            },
        }),
        columnHelper.accessor('enable_proration', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(billingPoliciesMessages.enabledProrate),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(permissionManageServicePlans
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: ({ row }) => {
                          const allowed =
                              !isServicePlanItemActive || row.original.status === BILLING_STATUS.SCHEDULED;
                          return allowed ? <CellServicePlanBillingPolicyActions row={row} /> : undefined;
                      },
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<ServicePlanBillingPolicy, unknown>
            columns={columns}
            data={servicePlanBillingPolicies}
            semantics={{
                title: formatMessage(billingPoliciesMessages.billingPolicies),
                entryName: formatMessage(billingPoliciesMessages.policy).toLowerCase(),
                entryNames: formatMessage(billingPoliciesMessages.policies).toLowerCase(),
                placeholder: formatMessage(billingPoliciesMessages.policy).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateServicePlanBillingPolicyFormModal }),
            }}
        />
    );
};

export default ServicePlanBillingPoliciesTable;
